<template>
  <div id="coder-picker" ref="coder-picker">
    <button 
      ref="button" 
      :class="{ 'assignee': true, 'disabled': disabled }" 
      @click="onShowCodersList"
    >
      <div v-if="hasCoder" class="img-container" :style="`background-image: url(${coder.photo})`" v-on:mouseover="onShowCoderPreview" v-on:mouseleave="onHideCoderPreview">{{coderPreview.initials}}</div>
      <i v-else class="fas fa-user-circle"></i>
    </button>

    <div 
      v-if="showCoderList && permissionAssignArticles !== 'no'" 
      ref="codersList" 
      class="available-assignees show" 
      v-click-outside="onHideCodersList"
    >
      <div v-if="isDisabled" class="alert alert-warning mb10">
        {{ isDisabledMessage }}
      </div>

      <load-content :loading="loading" :class="{ 'rm250': loading }">
        <div class="title">{{$t('articles.form.coder.change')}}</div>
        <MultiSelect
          :id='Math.random() + "_assignee"'
          :options="optionsList"
          :show-labels=false
          :allowEmpty="allowEmpty"
          label="label"
          track-by="value"
          :showNoResults=false
          :placeholder="$t('articles.form.coder.select')"
          :disabled="isDisabled"
          @select=onSelectCoder
          >
          <template slot="option" slot-scope="props">
            <div class="option">
              <div class="image" :style="`background-image: url(${coder.photo})`" :alt="props.option.name">{{nameInitials(props.option.label)}}</div>
              <div class="text">{{ props.option.label }}</div>
            </div>
          </template>
        </MultiSelect>
      </load-content>
    </div>

    <div ref="coder-preview" id="coder-preview" class="preview-wrapper preview--user open" v-if="hasCoder && showCoderPreview">
      <div class="header">
        <!-- <div class="img-container">
          <img :src="`/coders/${coderPreview.image}`" alt="John Doe">
        </div> -->
        <div class="image">{{coderPreview.initials}}</div>
        <div class="info">
          <div class="name">{{coderPreview.name}}</div>
          <!-- <div class="title">Report writer</div> -->
        </div>
      </div>
      <div class="body">
        <div class="meta">
          <div class="field">
            <div class="name">Assigned articles</div>
            <div class="value">{{coderPreview.assigned}}</div>
          </div>
          <div class="field">
            <div class="name">Maximum articles</div>
            <div class="value">{{coderPreview.max}}</div>
          </div>
          <div class="field">
            <div class="name">Last coded</div>
            <div class="value">{{coderPreview.last_coded}}</div>
          </div>
          <div class="field">
            <div class="name">Rating</div>
            <div class="value">{{coderPreview.rating}}</div>
          </div>
          <div class="field field--full-width">
            <div class="name">Language(s)</div>
            <div class="value">{{coderPreview.languages}}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import MultiSelect from 'vue-multiselect';
import _ from 'lodash';
import Moment from 'moment';
import CountriesList from 'countries-list';
import LoadContent from './load-content';

export default {
  props: {
    row: Object,
    coder: Object,
    options: Array,
    readonly: Boolean,
    change: Function,
    allowEmpty: {
      type: Boolean,
      required: false,
      default: true
    },
    isDisabled: {
      type: Boolean,
      required: false,
      default: true
    },
    isDisabledMessage: {
      type: String,
      required: false,
      default: ''
    },
    loading: {
      type: Boolean,
      default: () => false
    }
  },

  components: {
    MultiSelect,
    LoadContent
  },

  data: function() {
    const { coder, readonly } = this;

    const showCoderList = false,
          showCoderPreview = false;

    return {
      showCoderList,
      showCoderPreview,
      disabled: readonly,
    }
  },

  methods: {
    formatOption: function(coder) {
      return {
        value: coder.id,
        label: coder.name,
        image: coder.photo
      }
    },

    onShowCodersList: function() {
      if (this.permissionAssignArticles !== 'no' && this.readonly == false) {
        setTimeout(function() {
          this.onHideCoderPreview();
          this.showCoderList = true;
          this.$nextTick(function() {
            this.setPosition();
          })
        }.bind(this), 10);
      }
    },

    onHideCodersList: function() {
      this.showCoderList = false;
    },

    onSelectCoder: function(val) {     
      const { row, change } = this;
      change(val, row);
      this.onHideCodersList();
    },

    onShowCoderPreview: function() {
      if (this.showCoderList === false) {
        this.showCoderPreview = true;
        this.$nextTick(function() {
          this.setPreviewPosition();
        }.bind(this))
      }
    },

    onHideCoderPreview: function() {
      this.showCoderPreview = false;
    },

    nameInitials: function(name) {
      let initials = '';

      if (name) {
        const coderNameSplit = name.trim().split(' ');
        if (coderNameSplit.length > 1) {
          initials = coderNameSplit[0][0].toUpperCase() + coderNameSplit[coderNameSplit.length - 1][0].toUpperCase();
        } else {
          initials = coderNameSplit[0][0].toUpperCase();
        }
      }
      
      return initials;
    },

    setPosition: function() {
      const list = this.$refs.codersList;
      const button = this.$refs.button;
      const rect = button.getBoundingClientRect();

      const buttonTop = rect.top,
            buttonLeft = rect.left,
            buttonHeight = rect.height;

      /*
      const buttonTop = button.getBoundingClientRect().top;
      const buttonLeft = button.getBoundingClientRect().left;
      const buttonWidth = button.getBoundingClientRect().width;
      const buttonHeight = button.getBoundingClientRect().height;
      */
      
      list.style.top = (buttonTop + buttonHeight) + 'px';
      list.style.left = buttonLeft + 'px';

      let { top, left, right, bottom/*, width, height */ } = list.getBoundingClientRect();

      if (right >= (window.innerWidth || document.documentElement.clientWidth)) {
        const diff = right - (window.innerWidth || document.documentElement.clientWidth);
        left = left - diff;
        list.style.left = left + 'px';
      }

      if (bottom >= (window.innerHeight || document.documentElement.clientHeight)) {
        const diff = bottom - (window.innerHeight || document.documentElement.clientHeight);
        top = top - diff;
        list.style.top = top + 'px';
      }
    },

    setPreviewPosition: function() {
      const child = this.$refs['coder-preview'];
      const relative = this.$refs['coder-picker'];

      const offset = {
        top: -40,
        left: 10
      }

      const { top, left, right, bottom, width, height } = relative.getBoundingClientRect();
      child.style.top = (top + offset.top) + 'px';
      child.style.left = (left + width + offset.left) + 'px';

      // Check if it is outside the window.
      const childBounds = child.getBoundingClientRect();
      const windowOffset = 10;
      
      if (childBounds.bottom >= (window.innerHeight || document.documentElement.clientHeight) - windowOffset) {
        const diff = childBounds.bottom - (window.innerHeight || document.documentElement.clientHeight);
        child.style.top = (childBounds.top - diff - windowOffset) + 'px';
      }

      if (childBounds.right >= (window.innerWidth || document.documentElement.clientWidth) - windowOffset) {
        child.style.left = (left - childBounds.width - offset.left) + 'px';
      }
    },

    handleScroll: function() {
      this.onHideCoderPreview();
      this.onHideCodersList();
    }
  },

  computed: {
    ...mapGetters('user', ['permissionAssignArticles']),
    buttonClasses() {
      return {
        'assignee': true,
        'empty': this.hasCoder
      }
    },

    optionsList() {
      let options = this.options.map(option => this.formatOption(option));
      if (!this.allowEmpty) options = options.filter(option => option.value > 0);

      return options;
    },

    hasCoder() {
      return !_.isEmpty(this.coder);
    },

    value() {
      const value = this.formatOption(this.coder);
      return value.value == undefined ? null : value;
    },

    coderPreview() {
      const { coder } = this;
      
      const initials = this.nameInitials(coder.name);
      
      const allLanguages = CountriesList.languages;
      const coderLanguages = coder && coder.languages ? coder.languages.filter(l => l != '') : [];
      const languages = coderLanguages.map(l => allLanguages[l].name).join(',  ')

      return {
        name: coder.name,
        initials,
        image: coder.photo,
        assigned: coder.articles,
        max: coder.max_articles || 'N/A',
        languages,
        rating: coder.rating || 'N/A',
        last_coded: Moment(coder.last_coded).isValid() ? Moment(coder.last_coded).format('D MMM YYYY') : 'N/A'
      }
    },
  },

  // watch: {
  //   value: {
  //     handler(val) {
  //       const coder = this.options.find(c => c.id == val.value);
  //       this.coder = coder;
  //       this.hasCoder = this.coder.id !== 0 ;
  //     }
  //   }
  // },

  mounted() {
    window.addEventListener('scroll', this.handleScroll);
  },

  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll);
  }
}
</script>

<style lang="scss">
  #coder-picker {
    // min-width: 200px;
    margin: 0 auto;
    position: relative;

    .assignee {
      width: 26px;
      position: relative;
      margin: 0 auto;
      background: none;
      border: none;
      outline: none;
      display: block;
      padding: 0;
      cursor: pointer;

      &.disabled {
        cursor: default;
      }

      .img-container {
        width: 26px;
        height: 26px;
        position: relative;
        overflow: hidden;
        border-radius: 50%;
        background-size: cover;
        background-repeat: no-repeat;
        background-position: center center;
        background-color: $vis-primary-color;
        color: #FFF;
        line-height: 26px;
        font-size: 12px;
        font-weight: bold;
      }

      i {
        font-size: 16px;
      }
    }

    .available-assignees {
      background-color: #FFF;
      padding: 20px;
      position: absolute;
      box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.5);
      border-radius: 4px;
      z-index: 2;
      width: 240px;

      .option {
        display: flex;
        align-items: center;
        flex-flow: row nowrap;

        .image {
          position: relative;
          width: 30px;
          height: 30px;
          margin-right: 10px;
          border-radius: 50%;
          overflow: hidden;
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
          background-color: $vis-primary-color;
          color: #FFF;
          line-height: 30px;
          font-size: 12px;
          font-weight: 700;
          text-align: center;
        }
      }
    }

    .multiselect {
      width: 100% !important;

      .multiselect__select {
        top: 3px;
      }

      .multiselect__option--selected {
        color: #27a8b1;
        font-weight: bold;
        font-size: 13px;

        .image {
          padding: 2px;
          line-height: 24px;
          border: solid 1px #ec9148;
          background-clip: content-box; /* support: IE9+ */
          background-color: #ec9148;
        }
      }

      .multiselect__tags {
        padding: 4px 40px 4px 4px;
        background-color: #f1f1f1;
        border-radius: 5px;
        display: flex;
        align-items: center;
        // justify-content: center;
      }

      .multiselect__input,
      .multiselect__single,
      .multiselect__placeholder {
        background: none;
        font-size: 12px;
      }

      .multiselect__input {
        @include placeholder;
      }

      .multiselect__content-wrapper {
        border: 1px solid #CCC;
      }
    }
  }

  #coder-preview {
    position: fixed;
    z-index: 2;
    //bottom: 20px;
    left: 20px;
    background-color: #FFF;
    box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.5);
    padding: 20px;
    box-sizing: border-box;
    width: 400px;

    .header {
      display: flex;
      align-items: center;

      .image {
        width: 100px;
        height: 100px;
        background-position: center center;
        background-repeat: no-repeat;
        background-size: cover;
        background-color: $vis-primary-color;
        color: #FFF;
        line-height: 100px;
        text-align: center;
        font-weight: 700;
        font-size: 34px;
      }

      // .img-container {
      //   width: 100px;
      //   height: 100px;
      //   position: relative;
      //   overflow: hidden;

      //   img {
      //     position: absolute;
      //     top: 50%;
      //     left: 50%;
      //     transform: translate(-50%, -50%);
      //   }
      // }

      .info {
        margin-left: 20px;

        .name {
          font-size: 16px;
          font-weight: 700;
        }

        .title {
          font-style: italic;
        }
      }
    }

    .body {
      .meta {
        margin-top: 10px;
        display: flex;
        flex-flow: row wrap;
        font-size: 13px;
        margin: -5px;

        .field {
          flex: 0 0 50%;
          margin-top: 10px;
          padding: 5px;
          max-width: 50%;
          box-sizing: border-box;

          &.field--full-width {
            flex: 0 0 100%;
            max-width: 100%;
          }

          .name {
            color: #888;
            font-weight: 700;
          }
        }
      }
    }
  }

  .table--datatable[class*=table--datatable-fixed-] {
    #coder-picker {
      .available-assignees {
        position: fixed;
        z-index: 3;
      }
    }
  }
</style>